import { config } from '../config';
import { store } from '../store';
import { handleSessionExpired } from './sessionHandler';
import { ICategory, ICategoryResponse, ICreateCategory } from '../types/category.types';

const API_URL = config.API_URL;

export interface ICategoryBasic extends ICategory {
    password_count: number;
    card_count: number;
    personal_count: number;
    bank_count: number;
    total_count: number;
    shared_by_email?: string;
    permission_level?: string;
    expires_at?: Date;
    is_shared: boolean;
}

export class CategoryService {
    private static async request<T>(
        endpoint: string,
        method: string = 'GET',
        body?: any
    ): Promise<T> {
        try {
            const response = await fetch(`${API_URL}/categories${endpoint}`, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: body ? JSON.stringify(body) : undefined,
                credentials: 'include', // Send cookies for session auth
            });

            if (!response.ok) {
                if (response.status === 401) {
                    handleSessionExpired();
                }
                let errorText = 'Unknown error';
                try {
                    errorText = await response.text();
                } catch (e) {
                    errorText = response.statusText || 'Unknown error';
                }
                throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
            }

            return response.json();
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    static async getAllCategories(workspaceId?: string): Promise<ICategory[]> {
        return this.request<ICategory[]>(`?workspace_id=${workspaceId}`);
    }

    static async createCategory(category: ICreateCategory): Promise<ICategory> {
        return this.request<ICategory>('/', 'POST', category);
    }

    static async updateCategory(id: string, category: Partial<ICreateCategory>): Promise<ICategory> {
        return this.request<ICategory>(`/${id}`, 'PUT', category);
    }

    static async deleteCategory(id: string, deleteItems: boolean): Promise<void> {
        return this.request<void>(`/${id}?deleteItems=${deleteItems}`, 'DELETE');
    }

    static async getOrCreateCategory(folderName: string, workspaceId?: string): Promise<string | null> {
        if (!folderName) return null;
        
        try {
            // First try to find existing category
            const categories = await this.getAllCategories(workspaceId);
            const existingCategory = categories.find(
                cat => cat.name.toLowerCase() === folderName.toLowerCase()
            );
            
            if (existingCategory) {
                return existingCategory.id;
            }

            // If not found, create new category
            const newCategory = await this.createCategory({
                name: folderName,
                description: `Imported from folder: ${folderName}`,
                color: '#' + Math.floor(Math.random()*16777215).toString(16), // Random color
                icon: 'folder', // Default icon
                workspace_id: workspaceId || undefined
            });

            return newCategory.id;
        } catch (error) {
            console.error('Error in getOrCreateCategory:', error);
            return null;
        }
    }

    static async fetchCategoriesWithEntries(workspaceId?: string): Promise<ICategory[]> {
        return this.request<ICategory[]>(`/entries?workspace_id=${workspaceId}`);
    }

    static async getCategoryEntries(categoryId: string): Promise<ICategoryResponse> {
        return this.request<ICategoryResponse>(`/${categoryId}/entries`);
    }

    static async getAllCategoriesWithCount(): Promise<any[]> {
        return this.request<any[]>('/get-count');
    }
    
    static async getCategoryItems(categoryId: string | null, workspaceId?: string): Promise<any> {
        // For uncategorized items, use "uncategorized" as the path parameter
        const endpoint = categoryId === null ? `/uncategorized/get-items?workspace_id=${workspaceId}` : `/${categoryId}/get-items?workspace_id=${workspaceId}`;
        return this.request<any>(endpoint);
    }

    static async getAllCategoriesBasic(workspaceId?: string): Promise<ICategoryBasic[]> {
        return this.request<ICategoryBasic[]>(`/basic?workspace_id=${workspaceId}`);
    }

}